import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  AspectRatio,
  Box,
  Button,
  Container,
  Flex,
  Grid,
  GridItem,
  HStack,
  ListItem,
  OrderedList,
  UnorderedList,
  chakra,
  useDisclosure,
} from '@chakra-ui/react';
import { AddIcon, MinusIcon } from '@chakra-ui/icons';
import { manualAppLinks, manualInstallationLinks } from './config';

import HeaderNavMenu from './components/HeaderNavMenu';
import InfoImg from '../../assets/images/info.png';
import LogoFooter from '../../assets/images/logo-2.png';
import ManualLink from './components/ManualLink'
import MeasureSectionImage from '../../assets/images/svg/measure-section-image.svg';
import MeduxLogo from '../../assets/images/svg/medux-logo.svg';
import MicrosystemLogo from '../../assets/images/svg/microsystem-logo.svg';
import PrivacyImg from '../../assets/images/privacy.png';
import React from 'react';
import Results from '../../assets/images/results.png';
import ScrollToHashElement from '../../components/common/ScrollToHashElement';
import SpeedTest from '../../assets/images/svg/speedtest.svg';
import SpeedTestInProgress from '../../assets/images/svg/speedtest-in-progress.svg';
import SubscriptionModal from './components/SubscriptionModal';
import styled from '@emotion/styled';

const OutlineIconStyle = styled.span`
  border: 2px solid #1ca5cc;
  display: flex;
  padding: 5px;
  border-radius: 50%;
  background: #1ca5cc;
  &:hover {
    background: white;
    transition: background 0.5s ease-out;
  }
  &:hover svg {
    color: #1ca5cc;
    transition: background 0.5s ease-out;
  }
`;

const StyledFirstSectionBg = styled.div`
  background-color: #f6ecf4;
  color: #0b4482;
  @media (min-width: 1280px) {
    background-image: radial-gradient(circle, #a5afcb 0%, #f6ecf4 60%);
    background-size: 50% 50%;
    background-repeat: no-repeat;
    background-position-x: 100%;
    background-position-y: top;
  }
`;

const StyledAccordionTitle = styled.h3`
  color: #0b4582;
  font-size: 22px;
  font-weight: 700;
  @media (max-width: 1080px) {
    font-size: 18px;
  }
  @media (max-width: 640px) {
    font-size: 16px;
  }
`;

const StyledYellowButton = chakra(Button, {
  baseStyle: {
    fontSize: '16px',
    background: '#FFCF5C',
    borderRadius: '8px',
    boxSizing: 'content-box',
    marginTop: '80px',
    padding: '8px 24px',
    color: 'primary.100',
    fontWeight: '700',
    ':hover': {
      background: '#FFB810',
    },
    '@media (max-width: 640px)': {
      padding: '4px 12px',
      marginTop: '40px',
      marginBottom: '40px',
    },
    '@media (max-width: 520px)': {
      padding: '4px',
      marginTop: '40px',
      boxSizing: 'border-box',
      fontSize: '12px',
      marginBottom: '40px',
    },
  },
});

export const HomePage = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <>
      <StyledFirstSectionBg>
        <div className='px-4 md:px-8 md:py-6 py-4 mx-auto max-w-screen-xl'>
          <HeaderNavMenu />
          <div className='justify-between gap-10 pb-8 mt-12 lg:mt-24 lg:pb-32 lg:flex'>
            <div className='max-w-[700px]'>
              <div className=''>
                <h1 className='text-2xl font-bold xl:leading-tight lg:text-4xl xl:text-5xl'>
                  Bienvenido al Sistema de Medición de
                  Velocidades Mínimas garantizadas.
                </h1>
                <h3 className='mt-14 text-xl lg:text-[28px] font-bold'>
                  ¿Quiere conocer cómo funciona su conexión a Internet red fija?
                </h3>
                <p className='mt-4 md:text-lg'>
                  Descargue la aplicación para medir las velocidades
                  instantáneas y las velocidades promedio si es de su
                  preferencia, todas de una manera muy sencilla.
                </p>
                <StyledYellowButton onClick={onOpen}>
                  Descargue la aplicación VMGChile
                </StyledYellowButton>
              </div>
            </div>
            <div className='flex flex-col items-end gap-5 mt-6 md:mt-12'>
              <div className='bg-[#E8F6FA] gap-2 md:gap-6 rounded-xl py-4 md:py-10 px-4 md:px-6 shadow-md flex items-center text-[#0B4582] lg:w-[525px] lg:mt-0'>
                <img
                  src={InfoImg}
                  alt='Información'
                  className='w-[56px] md:w-[90px]'
                />
                <div>
                  <h3 className='text-xl font-bold md:text-2xl'>Información</h3>
                  <p className='mt-1 text-sm md:mt-2 md:text-base'>
                    Consulte aquí toda la información sobre el proceso de
                    mediciones.
                  </p>
                </div>
                <SubscriptionModal isOpen={isOpen} onClose={onClose} />
              </div>
              <div className='bg-[#E8F6FA] gap-2 md:gap-6 rounded-xl py-4 md:py-10 px-4 md:px-6 shadow-md flex items-center text-[#0B4582] lg:w-[385px] lg:mt-0'>
                <img
                  src={SpeedTest}
                  alt='Test'
                  className='w-[66px] md:w-[102px]'
                />
                <div>
                  <h3 className='text-xl font-bold md:text-2xl'>Test</h3>
                  <p className='mt-1 text-sm md:mt-2 md:text-base text-[#0B4582]'>
                    <span className='font-semibold'>
                      ¿Quiere probar su conexión a internet?
                    </span>
                  </p>
                </div>
              </div>
              <div className='bg-[#E8F6FA] gap-2 md:gap-6 rounded-xl py-4 md:py-10 px-4 md:px-6 shadow-md flex items-center text-[#0B4582] lg:w-[475px] lg:mt-0'>
                <img
                  src={Results}
                  alt='Resultados'
                  className='w-[56px] md:w-[93px]'
                />
                <div>
                  <h3 className='text-xl font-bold md:text-2xl'>Resultados</h3>
                  <p className='mt-1 text-sm md:mt-2 md:text-base'>
                    Esta sección le permite ver y analizar el resultado de sus
                    mediciones.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </StyledFirstSectionBg>
      <div className='py-8 md:py-12 lg:py-24 text-[#0B4482] px-4 md:px-8'>
        <div className='mx-auto max-w-screen-xl'>
          <Flex className='flex justify-between flex-col lg:flex-row xl:px-8'>
            <Flex className='justify-center'>
              <h2
                id='whatIsOti'
                className='text-2xl font-bold md:text-3xl lg:text-5xl mb-6 lg:mb-0'
              >
                ¿Qué es OTI?
              </h2>
            </Flex>
            <Flex className='max-w-[746px] flex-col lg:ml-10 text-center lg:text-left'>
              <p className='font-bold md:text-[22px]'>
                El Organismo Técnico Independiente (OTI) es una institución
                creada por la Ley 21.046 para encargarse de medir la velocidad
                promedio que entregan los proveedores de internet (ISP) a sus
                clientes, siguiendo las normas de la Subsecretaría de
                Telecomunicaciones.
              </p>
              <p className='mt-2 overflow-hidden md:mt-8 md:text-lg text-ellipsis'>
                Dando cumplimiento a lo establecido en la Ley se adjudicó la
                licitación del Organismo Técnico Independiente (OTI) a la
                empresa Microsystem Medux Chile S.A (MyM), quién es el encargado
                del contrato para crear y operar el OTI hasta 2027.
              </p>
              <p className='mt-2 overflow-hidden md:mt-8 md:text-lg text-ellipsis'>
                El OTI desarrolló la primera herramienta de medición de las
                velocidades promedio (el Sistema de Medición de Velocidades
                Promedio de Internet).
              </p>
            </Flex>
          </Flex>
          <Container
            maxW='1216px'
            bg='#0B4582'
            color='white'
            className='py-8 md:py-12 lg:py-[70px] rounded-[20px] mt-[55px]'
          >
            <Grid
              templateColumns={'1fr 1px 1fr'}
              gap={4}
              className='justify-center'
            >
              <GridItem className='mx-auto my-auto'>
                <img src={MeduxLogo} />
              </GridItem>
              <GridItem bg='white' h='52px' w='1px'></GridItem>
              <GridItem className='mx-auto my-auto'>
                <img src={MicrosystemLogo} />
              </GridItem>
            </Grid>
          </Container>
        </div>
      </div>
      <div className='pt-4 md:pt-6 lg:pt-6 pb-8 md:pb-12 lg:pb-24 text-[#0B4482]'>
        <div className='mx-auto max-w-screen-xl'>
          <div className='max-w-4xl px-4 mx-auto text-center md:px-8'>
            <h2
              id='methodology'
              className='text-2xl font-bold md:text-3xl lg:text-5xl'
            >
              ¿Cómo funciona esta aplicación?
            </h2>
            <p className='mt-2 overflow-hidden md:mt-4 md:text-lg'>
              Esta aplicación permite la medición de velocidades instantáneas y
              promedio. Para calcular estas velocidades, la aplicación
              programa mediciones y usa recursos del equipo en segundo plano.
            </p>
          </div>
          <div className='grid-cols-2 gap-12 px-4 md:px-8 md:mt-6 md:grid lg:mt-16'>
            <div className='mt-4 text-center md:mt-0'>
              <img
                src={SpeedTest}
                alt='Velocidad instantánea'
                width={180}
                className='mx-auto'
              />
              <h3 className='mt-2 text-xl font-bold md:mt-4 md:text-2xl'>
                Velocidad instantánea
              </h3>
              <p className='mt-2 text-sm md:text-base'>
                La medición de velocidad instantánea es aquella que registra la
                velocidad del usuario en un instante determinado.
              </p>
            </div>
            <div className='mt-4 text-center md:mt-0'>
              <img
                src={SpeedTestInProgress}
                alt='Velocidades promedio'
                width={180}
                className='mx-auto'
              />
              <h3 className='mt-2 text-xl font-bold md:mt-4 md:text-2xl'>
                Velocidades promedio
              </h3>
              <p className='mt-2 text-sm md:text-base'>
                La medición de velocidad promedio está compuesta por una serie
                de mediciones instantáneas que permiten que el sistema calcule
                las velocidades promedio representativas.
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className='py-10 bg-[#F6F9FF] text-[#0B4482] lg:px-8'>
        <div className='max-w-screen-xl px-4 mx-auto md:px-12 lg:px-0'>
          <Grid className='grid-cols-1 md:grid-cols-2' gap={2}>
            <GridItem className=''>
              <AspectRatio maxW='592px' className='mx-auto' ratio={16/9}>
                <iframe
                  className='rounded-lg'
                  title='¿Cómo se realiza cada medición video?'
                  src='https://280917944651-oti-landing.s3.us-west-2.amazonaws.com/Medición+en++windows.mp4'
                  allowFullScreen
                />
              </AspectRatio>
            </GridItem>
            <GridItem className='flex flex-col justify-center mt-8 md:mt-0'>
              <Box className='md:px-12'>
                <h3 className='text-xl md:text-[28px] font-bold'>
                  ¿Cómo se realiza cada medición?
                </h3>
                <p className='mt-2 md:mt-4 md:text-lg lg:mt-8'>
                  Para realizar cada medición, la aplicación debe capturar
                  información de su equipo y de los datos de su cuenta con su
                  operador de internet, para lo cual usted, el usuario, debe dar
                  su autorización.
                </p>
              </Box>
            </GridItem>
          </Grid>
        </div>
      </div>
      <div className='py-8 md:py-12 lg:py-24 text-[#0B4582]'>
        <div className='flex items-center grid-cols-2 px-4 mx-auto md:px-12 lg:px-8 max-w-screen-xl'>
          <div className='md:max-w-[512px] lg:max-w-[728px] md:mr-8'>
            <h3
              className='text-2xl font-bold text-center md:text-left lg:text-5xl'
              id='privacy'
            >
              Privacidad
            </h3>
            <p className='font-bold text-lg text-center  md:text-left md:text-[22px] mt-2'>
              ¿Qué pasa con mi privacidad?
            </p>
            <p className='mt-6 md:text-lg'>
              Para realizar las mediciones,
              <b>
                {' '}
                la aplicación necesita acceder a ciertos datos de su equipo
              </b>
              , tales como a cúal tipo de tecnología está conectado, cúal es el
              operador de servicio de internet, marca y modelo de equipo, cuanta
              memoria del equipo está en uso, qué porcentaje del procesador está
              en uso, si está conectado a la energía, etc.{' '}
              <b>
                Estos datos son denominados “Parámetros Ambientales de la
                Medición” y sólo con su autorización los podemos obtener.{' '}
              </b>
              En caso de no contar con su autorización, no podemos efectuar las
              mediciones.
            </p>
            <p className='mt-6 md:text-lg'>
              También
              <b> la aplicación necesita acceder a algunos datos personales</b>,
              para lo cual le debe dar consentimiento expreso para que la aplicación los
              solicite a su proveedor de servicio de internet. Entre estos datos personales
              se encuentra su email, detalles del plan contratado (nombre del plan, velocidad
              mínima comprometida, vigencia del contrato, estado del contrato, etc.). Tanto
              el consentimiento como la obligación del <b>OTI de garantizar la privacidad y
              seguridad de los datos personales de sus usuarios</b>, están establecidos en la <b>Ley
              N° 19.628 sobre Protección de la Vida Privada</b>. En consecuencia el <b>OTI asume el
              fuerte compromiso de velar por la privacidad de los datos personales involucrados
              en el proceso de medición</b>.
            </p>
            <p className='mt-6 md:text-lg'>
              Opcionalmente,{' '}
              <b>
                puedes autorizarnos a compartir los datos ambientales y
                personales de tus mediciones con la Subsecretaría de
                Telecomunicaciones de Chile (SUBTEL).
              </b>
            </p>
          </div>
          <div className='mt-8 md:mt-0'>
            <img
              src={PrivacyImg}
              alt='Privacidad'
              className='min-w-[260px] hidden md:md:inline-flex'
            />
          </div>
        </div>
      </div>
      <div className='py-8 md:py-12 lg:pb-24 md:pt-6 text-[#0B4482]'>
        <div className='mx-auto max-w-screen-xl'>
          <h2
            className='text-2xl font-bold text-center md:text-3xl lg:text-5xl mb-8 md:mb-14 lg:mb-[72px]'
            id='faq'
          >
            ¿Tiene dudas?
          </h2>
          <Accordion
            allowMultiple
            className='max-w-screen-xl text-left md:px-4 xl:px-8'
            defaultIndex={[0]}
          >
            <AccordionItem py={4} className='!border-0'>
              {({ isExpanded }) => (
                <>
                  <Box>
                    <AccordionButton
                      justifyContent='space-between'
                      className='text-left'
                    >
                      <StyledAccordionTitle>
                        ¿Como instalo esta Aplicación?
                      </StyledAccordionTitle>
                      {isExpanded ? (
                        <OutlineIconStyle>
                          <MinusIcon color='white' fontSize='12px' />
                        </OutlineIconStyle>
                      ) : (
                        <OutlineIconStyle>
                          <AddIcon color='white' fontSize='12px' />
                        </OutlineIconStyle>
                      )}
                    </AccordionButton>
                  </Box>
                  <AccordionPanel pb={4}>
                    Revise el manual de instalación y siga los pasos de acuerdo a su tecnología:
                    <HStack spacing={1} align='stretch'>
                      {manualInstallationLinks.map((manualLink, index) => (
                        <ManualLink key={manualLink.name} index={index} total={manualInstallationLinks.length} {...manualLink}/>
                      ))}
                    </HStack>
                  </AccordionPanel>
                </>
              )}
            </AccordionItem>
            <AccordionItem py={4} className='!border-0'>
              {({ isExpanded }) => (
                <>
                  <Box>
                    <AccordionButton
                      justifyContent='space-between'
                      className='text-left'
                    >
                      <StyledAccordionTitle>
                        ¿Como funciona esta Aplicación?
                      </StyledAccordionTitle>
                      {isExpanded ? (
                        <OutlineIconStyle>
                          <MinusIcon color='white' fontSize='12px' />
                        </OutlineIconStyle>
                      ) : (
                        <OutlineIconStyle>
                          <AddIcon color='white' fontSize='12px' />
                        </OutlineIconStyle>
                      )}
                    </AccordionButton>
                  </Box>
                  <AccordionPanel pb={4}>
                    Revise el manual de la aplicación para que pueda operarla e interpretar los resultados:
                    <HStack spacing={1} align='stretch'>
                      {manualAppLinks.map((manualLink, index) => (
                        <ManualLink key={manualLink.name} index={index} total={manualAppLinks.length} {...manualLink} />
                      ))}
                    </HStack>
                  </AccordionPanel>
                </>
              )}
            </AccordionItem>
            <AccordionItem className='!border-0' py={4}>
              {({ isExpanded }) => (
                <>
                  <Box>
                    <AccordionButton
                      justifyContent='space-between'
                      className='text-left'
                    >
                      <StyledAccordionTitle>
                        Consideraciones antes de medir:
                      </StyledAccordionTitle>
                      {isExpanded ? (
                        <OutlineIconStyle>
                          <MinusIcon color='white' fontSize='12px' />
                        </OutlineIconStyle>
                      ) : (
                        <OutlineIconStyle>
                          <AddIcon color='white' fontSize='12px' />
                        </OutlineIconStyle>
                      )}
                    </AccordionButton>
                  </Box>
                  <AccordionPanel pb={4}>
                    <OrderedList>
                      <ListItem mb={1}>
                        Es importante tener presente que las condiciones del
                        hardware, software o de red interna pueden afectar el
                        resultado de la medición.
                      </ListItem>
                      <ListItem mb={1}>
                        En el caso de las mediciones de velocidad promedio,
                        las mediciones programadas pueden ocasionar un uso
                        intensivo temporal del ancho de banda del usuario.
                      </ListItem>
                    </OrderedList>
                  </AccordionPanel>
                </>
              )}
            </AccordionItem>
            <AccordionItem className='!border-0' py={4}>
              {({ isExpanded }) => (
                <>
                  <Box>
                    <AccordionButton
                      justifyContent='space-between'
                      className='text-left'
                    >
                      <StyledAccordionTitle>
                        ¿Para qué sistemas operativos está disponible esta aplicación?
                      </StyledAccordionTitle>
                      {isExpanded ? (
                        <OutlineIconStyle>
                          <MinusIcon color='white' fontSize='12px' />
                        </OutlineIconStyle>
                      ) : (
                        <OutlineIconStyle>
                          <AddIcon color='white' fontSize='12px' />
                        </OutlineIconStyle>
                      )}
                    </AccordionButton>
                  </Box>
                  <AccordionPanel pb={4}>
                    <UnorderedList>
                      <ListItem mb={1}>
                        <b>Windows:</b> Versiones Windows 10 y Windows 11.
                      </ListItem>
                      <ListItem mb={1}>
                        <b>MacOS:</b> Versiones 12, 13, 14 y 15.
                      </ListItem>
                      <ListItem mb={1}>
                        <b>Linux (Ubuntu):</b> Versión 20 y 22. Y Rocky 9.4.
                      </ListItem>
                    </UnorderedList>
                  </AccordionPanel>
                </>
              )}
            </AccordionItem>
            <AccordionItem className='!border-0' py={4}>
              {({ isExpanded }) => (
                <>
                  <Box>
                    <AccordionButton
                      justifyContent='space-between'
                      className='text-left'
                    >
                      <StyledAccordionTitle>
                        ¿Me sirve cualquier cable de red para medir con esta aplicación?
                      </StyledAccordionTitle>
                      {isExpanded ? (
                        <OutlineIconStyle>
                          <MinusIcon color='white' fontSize='12px' />
                        </OutlineIconStyle>
                      ) : (
                        <OutlineIconStyle>
                          <AddIcon color='white' fontSize='12px' />
                        </OutlineIconStyle>
                      )}
                    </AccordionButton>
                  </Box>
                  <AccordionPanel pb={4}>
                    <UnorderedList>
                      <ListItem mb={1}>
                        Para medir velocidades menores a 100 Mbps, debes usar un cable de red categoría 5.
                      </ListItem>
                      <ListItem mb={1}>
                        Para medir velocidades mayores a 100 Mbps, y hasta 1 Gbps; debes usar un cable de red categoría 5e.
                      </ListItem>
                      <ListItem mb={1}>
                        Para medir velocidades de 2.5, 5 ó 10 Gbps; debes usar un cable de red categoría 6.
                      </ListItem>
                    </UnorderedList>
                  </AccordionPanel>
                </>
              )}
            </AccordionItem>
          </Accordion>
        </div>
      </div>
      <div className='py-8 md:py-12 lg:pb-24 md:pt-6 text-[#0B4482]'>
        <div className='mx-auto max-w-screen-xl px-4 md:px-8'>
          <Box
            bg='#F6F9FF'
            className='py-8 px-4 lg:px-[100px] lg:py-[60px] rounded-[20px]'
          >
            <h3 className='text-xl font-bold leading-tight md:text-3xl lg:text-[28px] mb-6 md:mb-[56px]'>
              Recomendaciones para asegurar la validez de sus mediciones:
            </h3>
            <UnorderedList styleType='none' className='!m-0'>
              <ListItem my={6} className='flex'>
                <span className='text-[#0B4582] text-xl lg:text-[28px] leading-7 font-bold pr-8'>
                  01
                </span>
                <span className='text-[16px] md:text-[18px]'>
                  Conecte su computador al router utilizando un{' '}
                  <b>cable de red</b>.
                </span>
              </ListItem>
              <ListItem my={6} className='flex'>
                <span className='text-[#0B4582] text-xl lg:text-[28px] leading-7 font-bold pr-8'>
                  02
                </span>
                <span className='text-[16px] md:text-[18px]'>
                  Asegúrese de que su computador está{' '}
                  <b>enchufado a la energía</b> y que no hay otros dispositivos
                  (televisores, consolas, computadores) conectados a Internet
                  mientras realiza la medición.
                </span>
              </ListItem>
              <ListItem my={6} className='flex'>
                <span className='text-[#0B4582] text-xl lg:text-[28px] leading-7 font-bold pr-8'>
                  03
                </span>
                <span className='text-[16px] md:text-[18px]'>
                  <b>Detenga todas las descargas en curso</b> en su computador y
                  en otros dispositivos conectados.
                </span>
              </ListItem>
              <ListItem my={6} className='flex'>
                <span className='text-[#0B4582] text-xl lg:text-[28px] leading-7 font-bold pr-8'>
                  04
                </span>
                <span className='text-[16px] md:text-[18px]'>
                  <b>Cierre cualquier aplicación o programa</b> que esté
                  ejecutando en paralelo.
                </span>
              </ListItem>
              <ListItem my={6} className='flex'>
                <span className='text-[#0B4582] text-xl lg:text-[28px] leading-7 font-bold pr-8'>
                  05
                </span>
                <span className='text-[16px] md:text-[18px]'>
                  <b>No comparta la conexión</b> a internet con otros
                  dispositivos y asegúrese de no usar ningún tipo de máquina
                  virtual o proxy.
                </span>
              </ListItem>
              <ListItem my={6} className='flex'>
                <span className='text-[#0B4582] text-xl lg:text-[28px] leading-7 font-bold pr-8'>
                  06
                </span>
                <span className='text-[16px] md:text-[18px]'>
                  <b>No corra el antivirus</b> mientras ejecuta las pruebas de velocidad.
                </span>
              </ListItem>
            </UnorderedList>
          </Box>
        </div>
      </div>
      <div className='bg-[#1CA5CC] text-white'>
        <div className='items-center grid-cols-2 gap-4 py-14 ml-auto md:grid mx-auto max-w-screen-xl'>
          <div className='py-4 lg:py-0 max-w-xl px-8'>
            <h3 className='text-xl font-bold leading-tight md:text-3xl lg:text-5xl'>
              Mida la velocidad de su conexión
            </h3>
            <p className='mt-2 lg:mt-6 md:text-lg'>
              Descargue la aplicación para medir las velocidades instantáneas y
              también las velocidades promedio si es de su preferencia, todas de
              una manera muy sencilla.
            </p>
            <StyledYellowButton onClick={onOpen}>
              Descargue la aplicación VMGChile
            </StyledYellowButton>
          </div>
          <img
            src={MeasureSectionImage}
            alt='Mide la velocidad de tu conexión'
            width={592}
          />
        </div>
      </div>
      <footer className='bg-[#0B4482] py-6 px-8'>
        <div className='flex items-center justify-between gap-4 mx-auto max-w-screen-xl xl:px-8'>
          <img src={LogoFooter} alt='Logo' width={98} />
          <p className='text-xs text-[#D1D5DB]'>
            &copy; 2024 All rights reserved
          </p>
        </div>
      </footer>
      <ScrollToHashElement />
    </>
  );
};

export default HomePage;
